import React, { useEffect, useState } from "react";
import { 
    Typography, 
    Box, 
    Container, 
    Grid, 
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Card,
    CardMedia,
    CardContent,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import { 
    useAuthRedirect, 
    getMyTickets, 
    eventStateEnum, 
    EnhancedErrorMessage, 
    Loading 
} from '@virtual-cinema/client-lib';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import UpcomingIcon from '@mui/icons-material/Update';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AudienceAppBar from '../common/app-bar';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

dayjs.extend(utc);
dayjs.extend(timezone);

// Group tickets by type (helper function)
const groupTicketsByType = (tickets) => {
    if (!tickets || !Array.isArray(tickets)) return {};
    
    return tickets.reduce((acc, ticket) => {
        if (!acc[ticket.name]) {
            acc[ticket.name] = { count: 0, price: ticket.price };
        }
        acc[ticket.name].count += 1;
        return acc;
    }, {});
};

export function MyTickets() {
    const [eventsData, setEventsData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTab, setSelectedTab] = useState('live');
    const accessToken = useAuthRedirect();
    const navigate = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        const fetchMyTickets = async () => {
            setIsLoading(true);
            try {
                const { data, error } = await getMyTickets(accessToken);

                if (error) {
                    throw new Error(error.message || "Failed to retrieve tickets info");
                } else {
                    setEventsData(data);
                }
            } catch (error) {
                setError("Failed to fetch tickets. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchMyTickets();
    }, [accessToken]);

    // Separate tickets by event state (Live, Upcoming, Past)
    const getSectionedEvents = () => {
        if (!eventsData) return { live: [], upcoming: [], past: [] };

        const live = eventsData.filter(event => event.info.state === eventStateEnum.Active);
        const upcoming = eventsData.filter(event => event.info.state === eventStateEnum.Published);
        const past = eventsData.filter(event => [eventStateEnum.Completed, eventStateEnum.Canceled].includes(event.info.state));

        return { live, upcoming, past };
    };

    const { live, upcoming, past } = getSectionedEvents();

    // Filter events based on search term and selected tab
    const getFilteredEvents = () => {
        if (!eventsData) return [];
        
        let filtered = [];
        
        switch (selectedTab) {
            case 'live':
                filtered = live;
                break;
            case 'upcoming':
                filtered = upcoming;
                break;
            case 'past':
                filtered = past;
                break;
            default:
                filtered = eventsData;
        }

        if (searchTerm) {
            filtered = filtered.filter(event => 
                event.info.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // For past events, sort most recent first
        if (selectedTab === 'past') {
            return filtered.sort((a, b) => 
                new Date(b.info.valid_from) - new Date(a.info.valid_from)
            );
        }
        
        // For other tabs, sort by date ascending
        return filtered.sort((a, b) => 
            new Date(a.info.valid_from) - new Date(b.info.valid_from)
        );
    };

    const filteredEvents = getFilteredEvents();

    const getStatusChip = (state) => {
        switch (state) {
            case eventStateEnum.Active:
                return <Chip 
                    label="Live Now" 
                    color="error"
                    size="small"
                    icon={<LiveTvIcon />}
                />;
            case eventStateEnum.Published:
                return <Chip 
                    label="Upcoming" 
                    color="primary"
                    size="small"
                    icon={<UpcomingIcon />}
                />;
            case eventStateEnum.Completed:
                return <Chip 
                    label="Completed" 
                    color="default"
                    size="small"
                    icon={<EventIcon />}
                />;
            case eventStateEnum.Canceled:
                return <Chip 
                    label="Canceled" 
                    color="default"
                    size="small"
                />;
            default:
                return null;
        }
    };

    const formatEventDate = (dateString) => {
        return dayjs.utc(dateString).local().format('ddd, MMM D, YYYY h:mm A');
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ flexGrow: 1, bgcolor: 'background.default', color: 'text.primary', minHeight: '100vh' }}>
                <AudienceAppBar />
                <Container sx={{ mt: 4 }} maxWidth="xl">
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h4" gutterBottom>
                                My Tickets
                            </Typography>
                        </Box>
                        <Typography color="text.secondary">
                            Access all your upcoming, live, and past event tickets
                        </Typography>
                    </Box>

                    {/* Search and Filters */}
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search tickets by event name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Tabs 
                            value={selectedTab}
                            onChange={(_, newValue) => setSelectedTab(newValue)}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab 
                                icon={<Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LiveTvIcon sx={{ mr: 1, fontSize: 18 }} />
                                    Live Now
                                </Box>} 
                                value="live" 
                            />
                            <Tab 
                                icon={<Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <UpcomingIcon sx={{ mr: 1, fontSize: 18 }} />
                                    Upcoming
                                </Box>} 
                                value="upcoming" 
                            />
                            <Tab 
                                icon={<Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <EventIcon sx={{ mr: 1, fontSize: 18 }} />
                                    Past Events
                                </Box>} 
                                value="past" 
                            />
                        </Tabs>
                    </Box>

                    {error ? (
                        <EnhancedErrorMessage message={error} />
                    ) : isLoading ? (
                        <Loading />
                    ) : (
                        <>
                            {filteredEvents.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, mb: 4 }}>
                                    <ConfirmationNumberIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" align="center">
                                        {selectedTab === 'past' 
                                            ? "No past events found."
                                            : selectedTab === 'live'
                                            ? "No live events found."
                                            : selectedTab === 'upcoming'
                                            ? "No upcoming events found."
                                            : "No events found."}
                                    </Typography>
                                    <Typography variant="body1" align="center" color="text.secondary">
                                        {searchTerm 
                                            ? "Try adjusting your search criteria."
                                            : "Explore the magic of movies and storytelling by purchasing tickets!"}
                                    </Typography>
                                </Box>
                            ) : (
                                selectedTab === 'past' ? (
                                    // Table view for past events
                                    <Box sx={{ width: '100%', mt: 2 }}>
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell width="30%" sx={{ fontWeight: 600 }}>Event Name</TableCell>
                                                        <TableCell width="25%" sx={{ fontWeight: 600 }}>Date</TableCell>
                                                        <TableCell width="15%" sx={{ fontWeight: 600 }}>Status</TableCell>
                                                        <TableCell width="20%" sx={{ fontWeight: 600 }}>Ticket Type</TableCell>
                                                        <TableCell width="10%" align="right" sx={{ fontWeight: 600 }}>Price</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredEvents.map((event) => {
                                                        const ticketTypes = groupTicketsByType(event.tickets);
                                                        const ticketEntries = Object.entries(ticketTypes);
                                                        
                                                        return (
                                                            <TableRow 
                                                                key={event.info.id}
                                                                hover
                                                                onClick={() => navigate(`/event/${event.info.id}`)}
                                                                sx={{ 
                                                                    cursor: 'pointer',
                                                                    height: 70
                                                                }}
                                                            >
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                        <Box
                                                                            sx={{
                                                                                width: 70,
                                                                                height: 50,
                                                                                borderRadius: 1,
                                                                                overflow: 'hidden',
                                                                                flexShrink: 0
                                                                            }}
                                                                        >
                                                                            <img 
                                                                                src={event.assets?.[0]?.bannerUrl || "/api/placeholder/120/68"}
                                                                                alt=""
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    objectFit: 'cover'
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                        <Typography variant="body1" fontWeight="500">
                                                                            {event.info.name}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {formatEventDate(event.info.valid_from)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {getStatusChip(event.info.state)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {ticketEntries.length > 0 ? (
                                                                        <Box>
                                                                            {ticketEntries.map(([name, details], idx) => (
                                                                                <Typography key={idx} variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                    <ConfirmationNumberIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                                                                    {name} ({details.count})
                                                                                </Typography>
                                                                            ))}
                                                                        </Box>
                                                                    ) : (
                                                                        'N/A'
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {ticketEntries.length > 0 ? (
                                                                        <Box>
                                                                            {ticketEntries.map(([name, details], idx) => (
                                                                                <Typography key={idx} variant="body2">
                                                                                    ${details.price}
                                                                                </Typography>
                                                                            ))}
                                                                        </Box>
                                                                    ) : (
                                                                        'N/A'
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                ) : (
                                    // Card view for live and upcoming events
                                    <Grid container spacing={3}>
                                        {filteredEvents.map((event) => (
                                            <Grid item xs={12} sm={6} md={4} key={event.info.id}>
                                                <Card 
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        height: '100%',
                                                        bgcolor: 'background.paper',
                                                        color: 'text.primary',
                                                        '&:hover': {
                                                            transform: 'scale(1.03)',
                                                            boxShadow: `0 0 15px ${theme.palette.primary.main}`,
                                                            transition: 'all 0.3s ease-in-out',
                                                            cursor: 'pointer'
                                                        },
                                                    }}
                                                    onClick={() => navigate(`/event/${event.info.id}`)}
                                                >
                                                    <CardMedia
                                                        component="img"
                                                        height="140"
                                                        image={event.assets?.[0]?.bannerUrl || "/api/placeholder/400/140"}
                                                        alt={event.info.name}
                                                    />
                                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                        <Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                <Typography variant="subtitle2" color="text.secondary">
                                                                    <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                                                    {dayjs.utc(event.info.valid_from).local().format('ddd, MMM D, YYYY')}
                                                                </Typography>
                                                                {getStatusChip(event.info.state)}
                                                            </Box>
                                                            
                                                            <Typography gutterBottom variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                                                                {event.info.name}
                                                            </Typography>
                                                            
                                                            {event.info.details?.summary && (
                                                                <Typography 
                                                                    variant="body2" 
                                                                    color="text.secondary"
                                                                    sx={{ 
                                                                        mb: 2,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: 'vertical'
                                                                    }}
                                                                >
                                                                    {event.info.details.summary}
                                                                </Typography>
                                                            )}
                                                            
                                                            <List dense>
                                                                {Object.entries(groupTicketsByType(event.tickets)).map(([ticketName, ticketInfo], index) => (
                                                                    <ListItem key={index} disableGutters>
                                                                        <ListItemText
                                                                            primary={
                                                                                <Typography variant="body2">
                                                                                    <ConfirmationNumberIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                                                                    {`${ticketName} (${ticketInfo.count} ticket${ticketInfo.count > 1 ? 's' : ''})`}
                                                                                </Typography>
                                                                            }
                                                                            secondary={`$${ticketInfo.price} each`}
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )
                            )}
                        </>
                    )}
                </Container>
            </Box>
        </Container>
    );
}

export default MyTickets;